import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ParameterTooltip Component
 * Displays an info button with tooltip showing parameter details
 * 
 * @param {Object} param - The parameter object from API spec
 * @param {string} paramType - The type category (path, query, header, body)
 */
const ParameterTooltip = ({ param, paramType }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  // Extract parameter information
  const paramName = param.name;
  const paramDataType = param.type || param.schema?.type || 'string';
  const paramDescription = param.description || 'No description available';
  const paramRequired = param.required || false;
  const paramDefault = param.default;
  const paramExample = param['x-example'] || param.example || paramDefault;
  const paramEnumValues = param['x-enum-values'] || param.enum;

  // Generate example if not provided
  const getDefaultExample = () => {
    if (paramExample) return paramExample;
    
    const lowerName = paramName.toLowerCase();
    
    // CocktailDB specific examples
    if (lowerName === 'threshold') return '4.5';
    if (lowerName === 'recipename' || lowerName === 'cocktail_title') return 'Margarita';
    if (lowerName === 'diet') return 'Vegan';
    if (lowerName === 'ingredient') return 'vodka';
    if (lowerName === 'cooking_action') return 'shake';
    if (lowerName === 'imageid') return 'allrecipes_3';
    if (lowerName === 'field') return 'Calories';
    
    // Specific examples based on parameter names
    if (lowerName.includes('id')) {
      if (lowerName.includes('pubchem')) return '1183';
      if (lowerName.includes('receptor')) return 'OR1A1';
      if (lowerName.includes('cocktail') || lowerName.includes('image')) return '100003';
      return '15683';
    }
    if (lowerName.includes('name')) return 'Mojito';
    if (lowerName.includes('input') || lowerName.includes('query') || lowerName.includes('search')) {
      return 'vodka';
    }
    if (lowerName.includes('source') || lowerName.includes('naturalsource')) return 'Coffee';
    if (lowerName.includes('category')) return 'vodka';
    if (lowerName.includes('limit') || lowerName.includes('page_size')) return '10';
    if (lowerName.includes('page')) return '1';
    if (lowerName.includes('min')) return '5';
    if (lowerName.includes('max')) return '100';
    if (lowerName.includes('serv')) return '4';
    if (lowerName.includes('time')) return '15';
    
    // Default examples by type
    switch (paramDataType) {
      case 'integer':
      case 'number':
        return '100';
      case 'boolean':
        return 'true';
      case 'array':
        return '[item1, item2]';
      default:
        return 'example_value';
    }
  };

  // Handle click toggle for mobile users
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsClicked(!isClicked);
  };

  // Show tooltip when hovered OR clicked
  const showTooltip = isHovered || isClicked;

  const example = getDefaultExample();

  // Format parameter location for display
  const getLocationDisplay = (location) => {
    const locationMap = {
      path: 'URL Path',
      query: 'Query Parameter',
      header: 'HTTP Header',
      body: 'Request Body',
      formData: 'Form Data'
    };
    return locationMap[location] || location;
  };

  // Close tooltip when clicking outside
  const handleMouseLeave = () => {
    setIsHovered(false);
    // Only close on mouse leave if not clicked
    if (!isClicked) {
      setIsClicked(false);
    }
  };

  return (
    <div 
      className="inline-block relative ml-1.5"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      <button 
        type="button"
        onClick={handleClick}
        className="text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 rounded-full p-0.5"
        aria-label={`Information about ${paramName} parameter`}
        title="Click for parameter info"
      >
        <FontAwesomeIcon icon={faInfoCircle} className="text-sm" />
      </button>
      
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -5 }}
            transition={{ duration: 0.15 }}
            className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 z-[9999] w-80 pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4">
              {/* Header */}
              <div className="flex items-center justify-between gap-2 pb-2 mb-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-indigo-600 dark:text-indigo-400">
                    {paramName}
                  </span>
                  {paramRequired ? (
                    <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-[10px] font-bold rounded uppercase tracking-wide">
                      Required
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-[10px] font-medium rounded uppercase tracking-wide">
                      Optional
                    </span>
                  )}
                </div>
                {isClicked && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsClicked(false); }}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 text-xs"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              {/* Content */}
              <div className="space-y-3">
                {/* Purpose */}
                <div>
                  <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                    Purpose
                  </div>
                  <div className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                    {paramDescription}
                  </div>
                </div>
                
                {/* Type & Location Row */}
                <div className="flex gap-4">
                  {/* Type */}
                  <div className="flex-1">
                    <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Data Type
                    </div>
                    <div className="text-xs">
                      <code className="px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded font-mono">
                        {paramDataType}
                      </code>
                    </div>
                  </div>
                  
                  {/* Location */}
                  <div className="flex-1">
                    <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Location
                    </div>
                    <div className="text-xs text-gray-700 dark:text-gray-300">
                      {getLocationDisplay(paramType)}
                    </div>
                  </div>
                </div>
                
                {/* Example */}
                {example && (
                  <div>
                    <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Example Value
                    </div>
                    <div>
                      <code className="text-xs px-2 py-1 bg-green-50 dark:bg-green-900 text-green-800 dark:text-green-200 rounded border border-green-200 dark:border-green-700 font-mono inline-block">
                        {example}
                      </code>
                    </div>
                  </div>
                )}

                {/* Allowed Values - show if enum values exist */}
                {paramEnumValues && paramEnumValues.length > 0 && paramEnumValues.length <= 8 && (
                  <div>
                    <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Allowed Values
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {paramEnumValues.slice(0, 6).map((value, idx) => (
                        <span 
                          key={idx}
                          className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                        >
                          {value}
                        </span>
                      ))}
                      {paramEnumValues.length > 6 && (
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">
                          +{paramEnumValues.length - 6} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Arrow pointer */}
              <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-[-1px]">
                <div className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-gray-200 dark:border-r-gray-700"></div>
                <div className="absolute top-0 left-[1px] w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-white dark:border-r-gray-900"></div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ParameterTooltip;
